import { Trans, useTranslation } from 'next-i18next';
import { ChartBarFillIcon, MagicwandFillIcon, SparklingFillIcon, SquareGridFillIcon } from '@/icons';
import { MAX_FILE_SIZE_MB } from '@/shared/features/board/constants/file';
import { useCountryCode } from '@/shared/hooks';
import { PLAN_FEATURES } from '@/shared/plan/consts';
import { type GroupPricingContent } from '../PricingSection1/groupPricingContent';
import { TableHeader } from './TableHeader';
import { TableSection } from './TableSection';
import { type FeatureSection } from './types';
const PREMIUM_QUIZ_TYPES_COPY = {
  label: {
    key: 'pricing:planComparison.premiumQuizTypes',
    defaultValue: '프리미엄 문제 유형'
  },
  tooltip: {
    key: 'pricing:planComparison.premiumQuizTypesTooltip',
    defaultValue: '다양한 방식으로 문제를 만들 수 있는 유형이에요. Pro 전용 문제 유형은 프리미엄으로 제공돼요.'
  },
  allTypes: {
    key: 'pricing:planComparison.allQuizTypes',
    defaultValue: '모든 문제 유형'
  }
} as const;

/**
 * 보드 첨부파일 용량 상한 — 플랜과 무관하게 동일해서 세 칸이 같은 값을 쓴다.
 * 업로드 검증과 같은 상수를 보므로 상한이 바뀌면 이 표도 같이 따라간다.
 */
const BOARD_FILE_UPLOAD_LIMIT = `${MAX_FILE_SIZE_MB}MB`;
export const PlanComparisonTable = ({
  groupPricingContent
}: {
  groupPricingContent: GroupPricingContent;
}) => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  const {
    countryCode,
    isKR
  } = useCountryCode();
  const planFeatures = PLAN_FEATURES[isKR ? 'KR' : 'GLOBAL'];
  const TABLE_DATA: FeatureSection[] = [{
    id: 'PREMIUM_FEATURES',
    title: t('pricing:planComparison.premiumFeatures'),
    icon: <SparklingFillIcon size="xl" className="[&>*]:fill-alternative" />,
    features: [{
      id: 'CONCURRENT_USERS',
      label: t('pricing:planComparison.concurrentUsers'),
      tooltipText: t('pricing:planComparison.concurrentUsersTooltip'),
      data: {
        FREE: {
          isSupported: true,
          text: t('pricing:planComparison.maxNum', {
            num: 200
          })
        },
        BASIC: {
          isSupported: true,
          text: t('pricing:planComparison.maxNum', {
            num: 200
          })
        },
        PRO: {
          isSupported: true,
          text: t('pricing:planComparison.maxNum', {
            num: 200
          })
        }
      }
    }, {
      id: 'PREMIUM_MAPS',
      label: t('pricing:planComparison.premiumMaps'),
      tooltipText: t('pricing:planComparison.premiumMapsTooltip'),
      data: {
        FREE: {
          isSupported: true,
          text: t('pricing:planComparison.someLimited')
        },
        BASIC: {
          isSupported: true,
          text: `+${t('pricing:planComparison.basicExclusiveMap')}`
        },
        PRO: {
          isSupported: true,
          text: `+${t('pricing:planComparison.allMaps')}`
        }
      }
    }, {
      id: 'PREMIUM_QUIZ_TYPES',
      label: t(PREMIUM_QUIZ_TYPES_COPY.label.key as never, {
        defaultValue: PREMIUM_QUIZ_TYPES_COPY.label.defaultValue
      }),
      tooltipText: t(PREMIUM_QUIZ_TYPES_COPY.tooltip.key as never, {
        defaultValue: PREMIUM_QUIZ_TYPES_COPY.tooltip.defaultValue
      }),
      data: {
        FREE: {
          isSupported: true,
          text: t('pricing:planComparison.someLimited')
        },
        BASIC: {
          isSupported: true,
          text: t('pricing:planComparison.someLimited')
        },
        PRO: {
          isSupported: true,
          text: <>
                  <span className="laptop-sm:hidden">
                    +{t('pricing:planComparison.allMaps')}
                  </span>
                  <span className="hidden laptop-sm:inline">
                    +
                    {t(PREMIUM_QUIZ_TYPES_COPY.allTypes.key as never, {
                defaultValue: PREMIUM_QUIZ_TYPES_COPY.allTypes.defaultValue
              })}
                  </span>
                </>
        }
      }
    }, {
      id: 'PRIVATE_QUIZ',
      label: t('pricing:planComparison.privateQuiz'),
      tooltipText: t('pricing:planComparison.privateQuizTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('PRIVATE_QUIZ')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('PRIVATE_QUIZ')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('PRIVATE_QUIZ')
        }
      }
    }, {
      id: 'CLASSROOM_POINT',
      label: t('common:pricing.planComparison.classroomPoint'),
      tooltipText: t('common:pricing.planComparison.classroomPointTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('CLASSROOM_POINT')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('CLASSROOM_POINT')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('CLASSROOM_POINT')
        }
      }
    }, {
      id: 'REMOVE_ADS',
      label: t('pricing:planComparison.removeAds'),
      tooltipText: t('pricing:planComparison.removeAdsTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('REMOVE_ADS')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('REMOVE_ADS')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('REMOVE_ADS')
        }
      },
      visibleCountries: ['ID']
    }, {
      id: 'WORKSHEET',
      label: t('pricing:planComparison.worksheet'),
      tooltipText: t('pricing:planComparison.worksheetTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('WORKSHEET')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('WORKSHEET')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('WORKSHEET')
        }
      }
    }, {
      id: 'CONTINUE_NEXT_QUIZSET',
      label: t('pricing:planComparison.continueNextQuiz'),
      tooltipText: t('pricing:planComparison.continueNextQuizTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('CONTINUE_NEXT_QUIZSET')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('CONTINUE_NEXT_QUIZSET')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('CONTINUE_NEXT_QUIZSET')
        }
      }
    }]
  }, {
    id: 'LEARNING_REPORT_FEATURES',
    title: t('pricing:planComparison.learningReport'),
    icon: <ChartBarFillIcon size="xl" className="fill-alternative" />,
    features: [{
      id: 'STUDENT_STATS',
      label: t('pricing:planComparison.studentStats'),
      tooltipText: t('pricing:planComparison.studentStatsTooltip'),
      data: {
        FREE: {
          isSupported: true
        },
        BASIC: {
          isSupported: true
        },
        PRO: {
          isSupported: true
        }
      }
    }, {
      id: 'QUESTION_STATS',
      label: t('pricing:planComparison.questionStats'),
      tooltipText: t('pricing:planComparison.questionStatsTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('LMS_PARTIAL')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('LMS_PARTIAL')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('LMS_PARTIAL')
        }
      }
    }, {
      id: 'OVERALL_STATS',
      label: t('pricing:planComparison.overallStats'),
      tooltipText: t('pricing:planComparison.overallStatsTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('LMS_PARTIAL')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('LMS_PARTIAL')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('LMS_PARTIAL')
        }
      }
    }, {
      id: 'AI_TUTOR_CHAT_HISTORY',
      label: t('pricing:planComparison.aiTutorChatHistory'),
      tooltipText: t('pricing:planComparison.aiTutorChatHistoryTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('LMS_PARTIAL')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('LMS_PARTIAL')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('LMS_PARTIAL')
        }
      },
      visibleCountries: ['KR']
    }]
  }, {
    id: 'QUIZ_AI_FEATURES',
    title: t('pricing:planComparison.quizAi'),
    icon: <MagicwandFillIcon size="xl" className="[&>*]:fill-alternative" />,
    features: [{
      id: 'AI_FROM_QUERY',
      label: <Trans t={t} i18nKey={'pricing:planComparison.aiFromQuery'} components={{
        Br: <br className="hidden laptop-sm:block" />
      }} />,
      tooltipText: t('pricing:planComparison.aiFromQueryTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_FROM_QUERY')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_FROM_QUERY')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_FROM_QUERY')
        }
      }
    }, {
      id: 'AI_FROM_FILE',
      label: <Trans t={t} i18nKey={'pricing:planComparison.aiFromFile'} components={{
        Br: <br className="hidden laptop-sm:block" />
      }} />,
      tooltipText: `${t('pricing:planComparison.aiFromFileTooltip')} (pdf, csv, txt, docx, pptx, hwp)`,
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_FROM_FILE')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_FROM_FILE')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_FROM_FILE')
        }
      }
    }, {
      id: 'AI_FROM_YOUTUBE',
      label: <Trans t={t} i18nKey={'pricing:planComparison.aiFromYoutube'} components={{
        Br: <br className="hidden laptop-sm:block" />
      }} />,
      tooltipText: t('pricing:planComparison.aiFromYoutubeTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_FROM_YOUTUBE')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_FROM_YOUTUBE')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_FROM_YOUTUBE')
        }
      }
    }, {
      id: 'AI_SIMILAR_QUIZ_GENERATION',
      label: t('pricing:planComparison.aiSimilarQuiz'),
      tooltipText: t('pricing:planComparison.aiSimilarQuizTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_SIMILAR_QUIZ_GENERATION')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_SIMILAR_QUIZ_GENERATION')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_SIMILAR_QUIZ_GENERATION')
        }
      }
    }, {
      id: 'AI_CHOICE_GENERATION',
      label: t('pricing:planComparison.aiChoiceGeneration'),
      tooltipText: t('pricing:planComparison.aiChoiceGenerationTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_CHOICE_GENERATION')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_CHOICE_GENERATION')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_CHOICE_GENERATION')
        }
      }
    }, {
      id: 'AI_QUIZSET_TRANSLATION',
      label: t('pricing:planComparison.aiTranslation'),
      tooltipText: t('pricing:planComparison.aiTranslationTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_QUIZSET_TRANSLATION')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_QUIZSET_TRANSLATION')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_QUIZSET_TRANSLATION')
        }
      }
    }, {
      id: 'AI_TUTOR',
      label: t('pricing:planComparison.aiTutor'),
      tooltipText: t('pricing:planComparison.aiTutorTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_TUTOR')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_TUTOR')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_TUTOR')
        }
      },
      visibleCountries: ['KR']
    }]
  }, {
    id: 'ZEP_BOARD',
    title: t('pricing:planComparison.zepBoard'),
    icon: <SquareGridFillIcon size="xl" className="[&>*]:fill-alternative" />,
    features: [{
      id: 'BOARD_UNLIMITED_CREATION',
      label: t('pricing:planComparison.unlimitedBoards'),
      tooltipText: t('pricing:planComparison.unlimitedBoardsTooltip'),
      data: {
        FREE: {
          isSupported: true
        },
        BASIC: {
          isSupported: true
        },
        PRO: {
          isSupported: true
        }
      }
    }, {
      id: 'BOARD_FILE_UPLOAD',
      label: t('pricing:planComparison.boardFileUpload'),
      tooltipText: t('pricing:planComparison.boardFileUploadTooltip'),
      data: {
        FREE: {
          isSupported: true,
          text: BOARD_FILE_UPLOAD_LIMIT
        },
        BASIC: {
          isSupported: true,
          text: BOARD_FILE_UPLOAD_LIMIT
        },
        PRO: {
          isSupported: true,
          text: BOARD_FILE_UPLOAD_LIMIT
        }
      }
    }, {
      id: 'BOARD_GUEST_ACCESS',
      label: t('pricing:planComparison.boardGuestAccess'),
      tooltipText: t('pricing:planComparison.boardGuestAccessTooltip'),
      data: {
        FREE: {
          isSupported: true
        },
        BASIC: {
          isSupported: true
        },
        PRO: {
          isSupported: true
        }
      }
    }]
  }];
  const sections = TABLE_DATA.map(section => ({
    ...section,
    features: section.features.filter(feature => feature.visibleCountries?.includes(countryCode) ?? true)
  }));
  return <div className="px-md tablet-sm:px-lg tablet-lg:px-2xl" data-sentry-component="PlanComparisonTable" data-sentry-source-file="PlanComparisonTable.tsx">
      <div className="min-w-[528px]">
        <TableHeader groupPricingContent={groupPricingContent} data-sentry-element="TableHeader" data-sentry-source-file="PlanComparisonTable.tsx" />
        {sections.map(section => <TableSection key={section.id} section={section} />)}
      </div>
    </div>;
};